import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHand } from '@fortawesome/pro-light-svg-icons/faHand';
import { faBadgeCheck } from '@fortawesome/pro-light-svg-icons/faBadgeCheck';
import { TypedMemo } from '../../common/types';
import { CatalogRowOwnership } from './types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogOwnershipPillProps {
  ownership: CatalogRowOwnership;
}

/**
 * How the row is labelled. Features are labelled rather than hidden: promoting
 * somebody else's single is legitimate, but it must never read as your own.
 *
 * The ids are spelled out: the extractor needs them static.
 */
const CatalogOwnershipPillComponent: React.FC<CatalogOwnershipPillProps> = ({ ownership }) => {
  const intl = useIntl();

  switch (ownership) {
    // Unreachable today — nothing can confirm a release without a connected
    // account. Drawn so the column is ready when something can.
    case CatalogRowOwnership.verified:
      return (
        <span className={clsx(styles.pill, styles.pillVerified)}>
          <FontAwesomeIcon icon={faBadgeCheck} />
          {intl.formatMessage({ id: 'app.sml.catalog.ownership.verified', defaultMessage: 'Verified' })}
        </span>
      );
    case CatalogRowOwnership.featured:
      return (
        <span className={styles.pill}>
          {intl.formatMessage({ id: 'app.sml.catalog.ownership.featured', defaultMessage: 'Featured' })}
        </span>
      );
    case CatalogRowOwnership.addedByYou:
      return (
        <span className={styles.pill}>
          {intl.formatMessage({ id: 'app.sml.catalog.ownership.added', defaultMessage: 'Added by you' })}
        </span>
      );
    case CatalogRowOwnership.claimed:
    default:
      return (
        <span className={clsx(styles.pill, styles.pillClaimed)}>
          <FontAwesomeIcon icon={faHand} />
          {intl.formatMessage({ id: 'app.sml.catalog.ownership.claimed', defaultMessage: 'Claimed' })}
        </span>
      );
  }
};

export const CatalogOwnershipPill = TypedMemo(CatalogOwnershipPillComponent);
